/**
 * Adapter between the chat widget's `Transport` contract and the Protheus BFF.
 *
 * This lives in the consumer, not in the widget. `config.transport` exists for
 * exactly this situation (`transports/index.ts`: a supplied transport wins over
 * the `protocol` registry), and none of the five built-ins fit:
 *
 *   - one `apiUrl` handles everything, but a conversation must be created first
 *     and the server-minted `chat_id` IS the agent session id
 *   - the client mints `conversationId` (`utils.ts`), the server mints the real one
 *   - the built-in body is `{message, conversationId, messages[], attachments[],
 *     stream}`, while the request schema is `{message}` with `extra="forbid"`
 *   - the built-in SSE reader expects `data:{delta}` / `[DONE]`; this backend sends
 *     `run.started` / `heartbeat` / `message.completed` / `error` and never `[DONE]`
 *   - there are no token deltas at all: one complete answer at the end of the turn
 *
 * Everything network-facing lives in `chat-bff-client.js`. This file is only the
 * mapping, which is why it is short.
 */

import type { Transport, TransportEvent, TransportRequest } from '../../shared/chat-widget';
import { ChatError, deleteConversation, sendMessage } from './chat-bff-client';

/** What the consumer needs to know about a turn, for analytics. */
export type TurnObserver = (
  event:
    | { type: 'sent'; messageLength: number }
    | { type: 'answered'; durationMs: number; answerLength: number }
    | { type: 'failed'; code: string; requestId: string; durationMs: number }
    | { type: 'cancelled'; elapsedMs: number }
) => void;

export interface ProtheusTransport {
  transport: Transport;
  /** The server-side chat id currently bound to a widget conversation, if any. */
  serverChatIdFor: (conversationId: string) => string | undefined;
  /**
   * Release a conversation upstream and forget the mapping.
   *
   * The widget's `reset()` has no server-side hook, so the consumer calls this
   * from `onConversationChange` when the id changes. Without it, "new chat" leaves
   * the old conversation alive on the agent, holding context and a session row that
   * nothing will ever remove — `DELETE /chat/conversations/{id}` is the only
   * deletion path that exists.
   */
  releaseConversation: (conversationId: string) => Promise<void>;
}

export function createProtheusTransport(observe?: TurnObserver): ProtheusTransport {
  /**
   * Widget conversation id -> server chat id.
   *
   * Deliberately a separate key from the widget's own `conversationId`. Yielding
   * `{type: 'meta', data: {conversationId: serverId}}` would work once and then
   * break everything: `useChat` treats that key as an instruction to replace its
   * conversation id, which orphans every entry in this map and loses the binding
   * on the very next message.
   */
  const serverChatIds = new Map<string, string>();

  const transport: Transport = async function* run(
    req: TransportRequest
  ): AsyncGenerator<TransportEvent> {
    const startedAt = Date.now();
    const text = req.message.content;
    let answered = false;
    let failed = false;

    observe?.({ type: 'sent', messageLength: text.length });

    try {
      const turn = sendMessage({
        message: text,
        // A callback, not a value: `sendMessage` owns the 404 recovery and may
        // replace the id mid-turn, and it needs the current binding at the moment
        // it actually sends.
        resolveChatId: () => serverChatIds.get(req.conversationId),
        // Must reach every fetch. Aborting is not cosmetic here — the disconnect
        // cancels the run server-side and frees one of only four capacity slots in
        // the entire agent deployment.
        signal: req.signal,
      });

      for await (const event of turn) {
        switch (event.type) {
          case 'chat':
            serverChatIds.set(req.conversationId, event.chatId);
            // Under a distinct key. See the comment on `serverChatIds`.
            yield { type: 'meta', data: { serverChatId: event.chatId } };
            break;

          case 'started':
            yield {
              type: 'meta',
              data: { requestId: event.requestId, startedAt, elapsedMs: 0 },
            };
            break;

          case 'elapsed':
            // The only honest progress signal available: the heartbeat carries
            // elapsed time and nothing else, so a percentage would be invented.
            yield { type: 'meta', data: { elapsedMs: event.elapsedMs, startedAt } };
            break;

          case 'answer':
            answered = true;
            observe?.({
              type: 'answered',
              durationMs: Date.now() - startedAt,
              answerLength: event.text.length,
            });
            // `message`, never `delta`. One complete answer arrives once, so the
            // pending bubble is replaced rather than appended to.
            yield { type: 'message', text: event.text };
            return;

          case 'failed': {
            failed = true;
            const error = event.error as ChatError;
            const code = error.code || 'INTERNAL_ERROR';
            const requestId = error.requestId || '';
            observe?.({
              type: 'failed',
              code,
              requestId,
              durationMs: Date.now() - startedAt,
            });
            // The code and id go into meta as well as the message, because the
            // widget flattens `error` to a string and support needs the id.
            yield { type: 'meta', data: { errorCode: code, requestId } };
            yield { type: 'error', error: error.message };
            return;
          }

          default:
            break;
        }
      }
    } catch (thrown) {
      // An abort is the visitor pressing stop or closing the panel, not a failure.
      // `useChat` already handles it by discarding the empty pending message, so
      // rethrowing is what keeps that path intact.
      if (req.signal.aborted) throw thrown;

      failed = true;
      const error = thrown as ChatError;
      const code = error?.code || 'INTERNAL_ERROR';
      observe?.({
        type: 'failed',
        code,
        requestId: error?.requestId || '',
        durationMs: Date.now() - startedAt,
      });
      yield { type: 'meta', data: { errorCode: code, requestId: error?.requestId || '' } };
      yield { type: 'error', error: error?.message || 'Ocorreu um erro inesperado.' };
    } finally {
      // Reached on abort too, because the generator is closed when the consumer
      // stops iterating. This is the only place a cancellation is observable:
      // `useChat` does not call `onError` for an abort.
      if (!answered && !failed) {
        observe?.({ type: 'cancelled', elapsedMs: Date.now() - startedAt });
      }
    }
  };

  return {
    transport,

    serverChatIdFor: (conversationId: string) => serverChatIds.get(conversationId),

    releaseConversation: async (conversationId: string) => {
      const chatId = serverChatIds.get(conversationId);
      if (!chatId) return;
      serverChatIds.delete(conversationId);
      try {
        await deleteConversation(chatId);
      } catch (ignored) {
        // Best effort. Failing to release upstream must not stop the visitor from
        // starting a new conversation; the agent's own retention is the backstop.
      }
    },
  };
}
